import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Chip,
  Collapse,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CalendarToday as CalendarTodayIcon,
  AccountTree as AccountTreeIcon,
  Business as BusinessIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { SmsFilterOptions, SmsTriggerFilters } from '@/types/sms';
import { RootState } from '@/store/store';
import {
  fetchSmsDependentSubChannelsRequested,
  clearSmsDependentSubChannels,
  fetchSmsDependentLOBsRequested,
  clearSmsDependentLOBs,
  fetchSmsDependentProductNamesRequested,
  clearSmsDependentProductNames,
  fetchSmsDependentChannelsRequested,
  clearSmsDependentChannels,
  clearPolicies
} from '@/store/slices/smsTriggerSlice';
import { FilterSectionHeader } from '@/components/filters/FilterSectionHeader';
import { FilterSectionGroup } from '@/components/filters/FilterSectionGroup';
import { AppliedFiltersChips, FilterChip } from '@/components/filters/AppliedFiltersChips';
import { CHOLA_COLORS } from '@/constants/filterStyles';
import { SingleSelect } from '@/components/ui/SingleSelect';
import { MultiSelect } from '@/components/ui/MultiSelect';

interface SmsFilterFormProps {
  filterOptions: SmsFilterOptions | null;
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  onFiltersChange: (filters: SmsTriggerFilters) => void;
  onPreview: (filters: SmsTriggerFilters) => void;
  previewLoading: boolean;
  currentFilters: SmsTriggerFilters;
}

export function SmsFilterForm({
  filterOptions,
  filterOptionsLoading,
  filterOptionsError,
  onFiltersChange,
  onPreview,
  previewLoading,
  currentFilters
}: SmsFilterFormProps) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { 
    dependentSubChannels, 
    dependentSubChannelsLoading,
    dependentChannels,
    dependentChannelsLoading,
    dependentProductNames,
    dependentProductNamesLoading
  } = useSelector(
    (state: RootState) => state.smsTrigger
  );
  const [filters, setFilters] = useState<SmsTriggerFilters>(currentFilters);
  const [redDate, setRedDate] = useState<Date | null>(null);
  const [expanded, setExpanded] = useState(true);

  // Update local state when currentFilters change
  useEffect(() => {
    setFilters(currentFilters);
    // Sync RED date from filters (use red_from or red_to, they should be the same)
    // Parse date string as local date to avoid timezone issues
    if (currentFilters.red_from) {
      const [year, month, day] = currentFilters.red_from.split('-').map(Number);
      setRedDate(new Date(year, month - 1, day));
    } else if (currentFilters.red_to) {
      const [year, month, day] = currentFilters.red_to.split('-').map(Number);
      setRedDate(new Date(year, month - 1, day));
    } else {
      setRedDate(null);
    }
  }, [currentFilters]);

  // Validate and clear RED date when RED Month changes
  useEffect(() => {
    if (filters.month && redDate) {
      const monthRange = getMonthDateRange(filters.month);
      if (monthRange) {
        // Check if current RED date is outside the selected month range
        if (redDate < monthRange.minDate || redDate > monthRange.maxDate) {
          // Clear RED date if it's outside the month range
          const newFilters = { ...filters };
          delete newFilters.red_from;
          delete newFilters.red_to;
          setRedDate(null);
          setFilters(newFilters);
          onFiltersChange(newFilters);
        }
      }
    } else if (filters.month && !redDate) {
      // If month is selected but no RED date, ensure filters are clean
      const newFilters = { ...filters };
      if (newFilters.red_from || newFilters.red_to) {
        delete newFilters.red_from;
        delete newFilters.red_to;
        setFilters(newFilters);
        onFiltersChange(newFilters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.month, redDate]);

  // Helper function to normalize filter value to array (handle backward compatibility)
  const normalizeToArray = (value: string | string[] | undefined): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [value];
  };

  // Get current filter values as arrays
  const channelArray = normalizeToArray(filters.channel);
  const subChannelArray = normalizeToArray(filters.sub_channel);
  const lobArray = normalizeToArray(filters.lob);
  const productNameArray = normalizeToArray(filters.product_name);

  // Watch for LOB changes and fetch dependent product names
  useEffect(() => {
    if (lobArray.length > 0) {
      // LOBs are selected, fetch dependent product names (union of all selected LOBs)
      dispatch(fetchSmsDependentProductNamesRequested(lobArray.length === 1 ? lobArray[0] : lobArray));
    } else {
      // LOBs are cleared, clear dependent product names
      dispatch(clearSmsDependentProductNames());
    }
  }, [lobArray.join(','), dispatch]);

  // Watch for product name changes and fetch dependent channels
  useEffect(() => {
    if (productNameArray.length > 0) {
      // Product names are selected, fetch dependent channels (union of all selected product names)
      dispatch(fetchSmsDependentChannelsRequested(productNameArray.length === 1 ? productNameArray[0] : productNameArray));
    } else {
      // Product names are cleared, clear dependent channels
      dispatch(clearSmsDependentChannels());
    }
  }, [productNameArray.join(','), dispatch]);

  // Watch for channel changes and fetch dependent sub-channels
  useEffect(() => {
    if (channelArray.length > 0) {
      // Channels are selected, fetch dependent sub-channels (union of all selected channels)
      dispatch(fetchSmsDependentSubChannelsRequested(channelArray.length === 1 ? channelArray[0] : channelArray));
    } else {
      // Channels are cleared, clear dependent sub-channels
      dispatch(clearSmsDependentSubChannels());
    }
  }, [channelArray.join(','), dispatch]);

  // Clear invalid product_name selections when dependent product names change
  useEffect(() => {
    if (productNameArray.length > 0 && dependentProductNames.length > 0) {
      const validProductNames = productNameArray.filter(name => dependentProductNames.includes(name));
      if (validProductNames.length !== productNameArray.length) {
        // Some product names are no longer valid, update filters
        const newFilters = { ...filters };
        newFilters.product_name = validProductNames.length > 0 ? (validProductNames.length === 1 ? validProductNames[0] : validProductNames) : undefined;
        setFilters(newFilters);
        onFiltersChange(newFilters);
      }
    }
  }, [dependentProductNames.join(','), productNameArray.join(','), onFiltersChange]);

  // Clear invalid channel selections when dependent channels change
  useEffect(() => {
    if (channelArray.length > 0 && dependentChannels.length > 0) {
      const validChannels = channelArray.filter(ch => dependentChannels.includes(ch));
      if (validChannels.length !== channelArray.length) {
        // Some channels are no longer valid, update filters
        const newFilters = { ...filters };
        newFilters.channel = validChannels.length > 0 ? (validChannels.length === 1 ? validChannels[0] : validChannels) : undefined;
        // Also clear sub_channel if channel changes
        if (newFilters.channel !== filters.channel) {
          newFilters.sub_channel = undefined;
        }
        setFilters(newFilters);
        onFiltersChange(newFilters);
      }
    }
  }, [dependentChannels.join(','), channelArray.join(','), onFiltersChange]);

  // Clear invalid sub_channel selections when dependent sub-channels change
  useEffect(() => {
    if (subChannelArray.length > 0 && dependentSubChannels.length > 0) {
      const validSubChannels = subChannelArray.filter(sc => dependentSubChannels.includes(sc));
      if (validSubChannels.length !== subChannelArray.length) {
        // Some sub-channels are no longer valid, update filters
        const newFilters = { ...filters };
        newFilters.sub_channel = validSubChannels.length > 0 ? (validSubChannels.length === 1 ? validSubChannels[0] : validSubChannels) : undefined;
        setFilters(newFilters);
        onFiltersChange(newFilters);
      }
    }
  }, [dependentSubChannels.join(','), subChannelArray.join(','), onFiltersChange]);

  // Helper function to format date as YYYY-MM-DD using local time (avoid timezone issues)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate date range from T-X/T+X format
  // Note: T-X means X days FROM today (future), T+X means X days BEFORE today (past)
  // This matches the database days_offset logic where positive = future, negative = past
  const calculateDateRange = (dateRange: string): { startDate: Date; endDate: Date } | null => {
    if (!dateRange || dateRange === 'custom') return null;
    
    const match = dateRange.match(/^T([+-])(\d+)$/);
    if (!match) return null;
    
    const operator = match[1];
    const days = parseInt(match[2], 10);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (operator === '-') {
      // T-X means RED = X days FROM today (future) - matches days_offset = +X
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + days);
      return { startDate: targetDate, endDate: targetDate };
    } else {
      // T+X means RED = X days BEFORE today (past) - matches days_offset = -X
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - days);
      return { startDate: targetDate, endDate: targetDate };
    }
  };

  // Parse RED Month string (format: "DEC-2025" or "DECEMBER-2025")
  const parseMonthString = (month: string): { year: number; month: number } | null => {
    if (!month) return null;
    
    // Month abbreviation to number mapping
    const monthMap: { [key: string]: number } = {
      'JAN': 1, 'JANUARY': 1,
      'FEB': 2, 'FEBRUARY': 2,
      'MAR': 3, 'MARCH': 3,
      'APR': 4, 'APRIL': 4,
      'MAY': 5,
      'JUN': 6, 'JUNE': 6,
      'JUL': 7, 'JULY': 7,
      'AUG': 8, 'AUGUST': 8,
      'SEP': 9, 'SEPTEMBER': 9,
      'OCT': 10, 'OCTOBER': 10,
      'NOV': 11, 'NOVEMBER': 11,
      'DEC': 12, 'DECEMBER': 12
    };
    
    // Split by hyphen or space
    const parts = month.split(/[- ]/);
    if (parts.length < 2) return null;
    
    const monthStr = parts[0].toUpperCase().trim();
    const yearStr = parts[1].trim();
    
    const monthNum = monthMap[monthStr];
    const year = parseInt(yearStr, 10);
    
    if (!monthNum || isNaN(year)) return null;
    
    return { year, month: monthNum };
  };

  // Get date range for a given month string
  const getMonthDateRange = (month: string): { minDate: Date; maxDate: Date } | null => {
    const parsed = parseMonthString(month);
    if (!parsed) return null;
    
    // Create minDate: first day of the month at 00:00:00
    const minDate = new Date(parsed.year, parsed.month - 1, 1, 0, 0, 0, 0);
    
    // Create maxDate: last day of the month at 23:59:59
    const maxDate = new Date(parsed.year, parsed.month, 0, 23, 59, 59, 999);
    
    return { minDate, maxDate };
  };

  const handleFilterChange = (field: keyof SmsTriggerFilters, value: any) => {
    const newFilters = { ...filters, [field]: value };
    
    // Normalize value to check if it's empty
    const isEmpty = !value || (Array.isArray(value) && value.length === 0);
    
    // Handle cascading filter clearing based on new dependency chain: LOB → Product → Channel → Sub Channel
    if (field === 'lob' && isEmpty) {
      // If LOB is being cleared, also clear product_name, channel, and sub_channel
      delete newFilters.product_name;
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'lob' && !isEmpty) {
      // If LOB is being changed (not cleared), clear downstream filters
      delete newFilters.product_name;
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'product_name' && isEmpty) {
      // If product_name is being cleared, also clear channel and sub_channel
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'product_name' && !isEmpty) {
      // If product_name is being changed (not cleared), clear downstream filters
      delete newFilters.channel;
      delete newFilters.sub_channel;
    } else if (field === 'channel' && isEmpty) {
      // If channel is being cleared, also clear sub_channel
      delete newFilters.sub_channel;
    } else if (field === 'channel' && !isEmpty) {
      // If channel is being changed (not cleared), clear sub_channel
      delete newFilters.sub_channel;
    }
    
    // Auto-fill RED from/to when date_range is selected
    if (field === 'date_range' && value && value !== 'custom') {
      const dateRange = calculateDateRange(value);
      if (dateRange) {
        // Use local date formatting to avoid timezone issues
        newFilters.red_from = formatDateLocal(dateRange.startDate);
        newFilters.red_to = formatDateLocal(dateRange.endDate);
        setRedDate(dateRange.startDate);
      }
    } else if (field === 'date_range' && (!value || value === 'custom')) {
      // Clear RED dates when date_range is cleared/reset
      delete newFilters.red_from;
      delete newFilters.red_to;
      setRedDate(null);
    }
    
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Handler for MultiSelect changes (extracts array from selected options)
  const handleMultiSelectChange = (field: 'channel' | 'sub_channel' | 'lob' | 'product_name') => (
    selected: { value: string; label: string }[]
  ) => {
    const values = selected.map(opt => opt.value);
    handleFilterChange(field, values.length > 0 ? (values.length === 1 ? values[0] : values) : undefined);
  };

  const handleRedDateChange = (date: Date | null) => {
    setRedDate(date);
    const newFilters = { ...filters };
    if (date) {
      // Set both red_from and red_to to the same date (exact match)
      // Use local date formatting to avoid timezone issues
      const dateStr = formatDateLocal(date);
      newFilters.red_from = dateStr;
      newFilters.red_to = dateStr;
    } else {
      // Clear RED dates
      newFilters.red_from = undefined;
      newFilters.red_to = undefined;
    }
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Dedicated handler to clear RED date filter
  const handleClearRedDate = useCallback(() => {
    const newFilters = { ...filters };
    delete newFilters.red_from;
    delete newFilters.red_to;
    setRedDate(null);
    setFilters(newFilters);
    onFiltersChange(newFilters);
  }, [filters, onFiltersChange]);

  const handleSearch = () => {
    onPreview(filters);
    setExpanded(false);
  };

  const clearFilters = () => {
    const clearedFilters: SmsTriggerFilters = {};
    setFilters(clearedFilters);
    setRedDate(null);
    dispatch(clearPolicies()); // Clear policies and reset pagination
    onFiltersChange(clearedFilters);
    // Don't automatically trigger search - let user click Search button
    // onPreview(clearedFilters);
  };

  // Count applied filters
  const countAppliedFilters = useCallback(() => {
    let count = 0;
    if (filters.month) count++;
    if (filters.date_range) count++;
    if (filters.red_from || filters.red_to) count++;
    if (channelArray.length > 0) count++;
    if (subChannelArray.length > 0) count++;
    if (lobArray.length > 0) count++;
    if (productNameArray.length > 0) count++;
    if (filters.policy_number) count++;
    if (filters.customer_mobile) count++;
    return count;
  }, [filters, channelArray.length, subChannelArray.length, lobArray.length, productNameArray.length]);

  // Generate applied filter chips
  const appliedFilterChips = useMemo((): FilterChip[] => {
    const chips: FilterChip[] = [];
    
    // 1. Non-dependent filters first: Month, Date Range, RED
    if (filters.month) {
      chips.push({
        key: 'month',
        label: `Month: ${filters.month}`,
        onDelete: () => handleFilterChange('month', undefined)
      });
    }
    if (filters.date_range) {
      // Use display_label from database
      const dateRangeObj = filterOptions?.date_ranges.find(dr => dr.code === filters.date_range);
      const displayLabel = dateRangeObj?.display_label || filters.date_range;
      chips.push({
        key: 'date_range',
        label: `Date Range: ${displayLabel}`,
        onDelete: () => handleFilterChange('date_range', undefined)
      });
    }
    if (filters.red_from || filters.red_to) {
      const dateStr = filters.red_from || filters.red_to || '';
      chips.push({
        key: 'red_date',
        label: `RED: ${dateStr}`,
        onDelete: handleClearRedDate
      });
    }

    // 2. Dependent filters: collect chips separately so we can control order
    const lobChips: FilterChip[] = [];
    const productChips: FilterChip[] = [];
    const channelChips: FilterChip[] = [];
    const subChannelChips: FilterChip[] = [];

    // LOB chips
    if (lobArray.length > 0) {
      const labelValue = lobArray.length === 1 ? lobArray[0] : lobArray.join(', ');
      lobChips.push({
        key: 'lob',
        label: `LOB: ${labelValue}`,
        onDelete: () => handleFilterChange('lob', undefined)
      });
    }

    // Product chips
    if (productNameArray.length > 0) {
      const labelValue = productNameArray.length === 1 ? productNameArray[0] : productNameArray.join(', ');
      productChips.push({
        key: 'product_name',
        label: `Product: ${labelValue}`,
        onDelete: () => handleFilterChange('product_name', undefined)
      });
    }

    // Channel chips
    if (channelArray.length > 0) {
      const labelValue = channelArray.length === 1 ? channelArray[0] : channelArray.join(', ');
      channelChips.push({
        key: 'channel',
        label: `Channel: ${labelValue}`,
        onDelete: () => handleFilterChange('channel', undefined)
      });
    }

    // Sub Channel chips
    if (subChannelArray.length > 0) {
      const labelValue = subChannelArray.length === 1 ? subChannelArray[0] : subChannelArray.join(', ');
      subChannelChips.push({
        key: 'sub_channel',
        label: `Sub Channel: ${labelValue}`,
        onDelete: () => handleFilterChange('sub_channel', undefined)
      });
    }

    // Push dependent chips in strict order: LOB → Product → Channel → Sub Channel
    chips.push(...lobChips, ...productChips, ...channelChips, ...subChannelChips);

    // 3. Remaining filters: policy_number, customer_mobile
    if (filters.policy_number) {
      chips.push({
        key: 'policy_number',
        label: `Policy: ${filters.policy_number}`,
        onDelete: () => handleFilterChange('policy_number', undefined)
      });
    }
    if (filters.customer_mobile) {
      chips.push({
        key: 'customer_mobile',
        label: `Mobile: ${filters.customer_mobile}`,
        onDelete: () => handleFilterChange('customer_mobile', undefined)
      });
    }
    
    return chips;
  }, [filters, channelArray, subChannelArray, lobArray, productNameArray, handleFilterChange]);

  const hasActiveFilters = countAppliedFilters() > 0;

  if (filterOptionsLoading) {
    return (
      <Card>
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading filter options...
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  }

  // Don't show error here - parent component handles error display
  // If there's an error, filterOptions will be null, so show warning
  if (!filterOptions) {
    return (
      <Card>
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">
            No filter options available
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card 
        sx={{ 
          mb: 1.5,
          mt: 0,
          transition: theme.transitions.create(['box-shadow']),
          '&:hover': {
            boxShadow: expanded ? theme.shadows[4] : theme.shadows[1],
          }
        }}
      >
        <FilterSectionHeader
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
          appliedCount={countAppliedFilters()}
          onClearAll={clearFilters}
          title="Filters"
          compact
        />

        {/* Applied Filters Display */}
        {hasActiveFilters && !expanded && (
          <AppliedFiltersChips chips={appliedFilterChips} />
        )}

        <Collapse in={expanded}>
          <Box sx={{ p: 1, bgcolor: 'background.paper' }}>
            <Grid container spacing={0.75}>
              {/* Top Filters Row - keep critical filters high on the page to avoid overlap with modal */}
              <Grid item xs={12} sm={6} md={3}>
                <SingleSelect
                  compact
                  label="RED Month"
                  options={filterOptions.months.map((month) => ({
                    value: month,
                    label: month
                  }))}
                  value={filters.month ? { value: filters.month, label: filters.month } : null}
                  onChange={(selected) =>
                    handleFilterChange('month', selected ? selected.value : undefined)
                  }
                  placeholder="Select month..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SingleSelect
                  compact
                  label="Date Range"
                  options={filterOptions.date_ranges
                    .filter((dr) => dr.code !== 'custom')
                    .map((dr) => ({
                      value: dr.code,
                      label: dr.display_label
                    }))}
                  value={
                    filters.date_range
                      ? {
                          value: filters.date_range,
                          label: filterOptions.date_ranges.find(dr => dr.code === filters.date_range)?.display_label || filters.date_range
                        }
                      : null
                  }
                  onChange={(selected) =>
                    handleFilterChange('date_range', selected ? selected.value : undefined)
                  }
                  placeholder="Select date range..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ width: '100%' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500, 
                      fontSize: '0.8125rem' 
                    }}
                  >
                    RED
                  </Typography>
                  <DatePicker
                    label=""
                    value={redDate}
                    onChange={handleRedDateChange}
                    format="dd-MMM-yyyy"
                    disabled={!!filters.date_range && filters.date_range !== 'custom'}
                    minDate={filters.month ? getMonthDateRange(filters.month)?.minDate : undefined}
                    maxDate={filters.month ? getMonthDateRange(filters.month)?.maxDate : undefined}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        placeholder: 'dd-MM-yyyy', // Explicit placeholder to override auto-generated one
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            minHeight: 32,
                            fontSize: '0.8125rem',
                          },
                          '& .MuiInputLabel-root': {
                            display: 'none',
                          },
                        },
                      },
                      popper: {
                        sx: {
                          zIndex: 10000, // Higher than MultiSelect dropdown (9999)
                        },
                        placement: 'bottom-start',
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 4],
                            },
                          },
                        ],
                      },
                    }}
                  />
                </Box>
              </Grid>

              {/* Business Filters Section */}
              <Grid item xs={12}>
                <FilterSectionGroup
                  title="Business Filters"
                  icon={BusinessIcon}
                  compact
                >
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Line of Business (LOB)"
                      options={filterOptions.lob_values.map((lob) => ({
                        value: lob,
                        label: lob
                      }))}
                      value={lobArray.map(lob => ({ value: lob, label: lob }))}
                      onChange={handleMultiSelectChange('lob')}
                      placeholder="Select LOBs..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Product Name"
                      options={dependentProductNames.map((name) => ({
                        value: name,
                        label: name
                      }))}
                      value={productNameArray.map(name => ({ value: name, label: name }))}
                      onChange={handleMultiSelectChange('product_name')}
                      placeholder={lobArray.length > 0 ? 'Select product names...' : 'Select LOB first'}
                      isLoading={dependentProductNamesLoading}
                      disabled={lobArray.length === 0}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Channel"
                      options={dependentChannels.map((channel) => ({
                        value: channel,
                        label: channel
                      }))}
                      value={channelArray.map(ch => ({ value: ch, label: ch }))}
                      onChange={handleMultiSelectChange('channel')}
                      placeholder={productNameArray.length > 0 ? 'Select channels...' : 'Select product first'}
                      isLoading={dependentChannelsLoading}
                      disabled={productNameArray.length === 0}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MultiSelect
                      compact
                      label="Sub Channel"
                      options={dependentSubChannels.map((subChannel) => ({
                        value: subChannel,
                        label: subChannel
                      }))}
                      value={subChannelArray.map(sc => ({ value: sc, label: sc }))}
                      onChange={handleMultiSelectChange('sub_channel')}
                      placeholder={channelArray.length > 0 ? 'Select sub channels...' : 'Select channel first'}
                      isLoading={dependentSubChannelsLoading}
                      disabled={channelArray.length === 0}
                    />
                  </Grid>
                </FilterSectionGroup>
            </Grid>

              {/* Additional Filters Section */}
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 0.5, 
                    color: CHOLA_COLORS.primary,
                    fontWeight: 500,
                    fontSize: '0.65rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                    Additional Filters
                  </Typography>
                  <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Policy Number"
                        placeholder="Enter policy number"
                        value={filters.policy_number || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleFilterChange('policy_number', e.target.value || undefined)
                        }
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            minHeight: 32,
                            fontSize: '0.8125rem',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.8125rem',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Customer Mobile"
                        placeholder="Enter mobile number"
                        value={filters.customer_mobile || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleFilterChange('customer_mobile', e.target.value || undefined)
                        }
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            minHeight: 32,
                            fontSize: '0.8125rem',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.8125rem',
                          },
                        }}
                      />
                    </Grid>
          </Grid>
                </Box>
              </Grid>

              {/* Apply Filters Button Section */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  mt: 0.5,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  pt: 0.5
                }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleSearch}
                    disabled={previewLoading}
                    startIcon={previewLoading ? <CircularProgress size={14} /> : <SearchIcon />}
                    sx={{
                      backgroundColor: CHOLA_COLORS.primary,
                      fontSize: '0.7rem',
                      py: 0.4,
                      px: 1,
                      minHeight: '32px',
                      '&:hover': {
                        backgroundColor: CHOLA_COLORS.hover
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    {previewLoading ? 'Loading...' : 'Search'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Card>
    </LocalizationProvider>
  );
}
